import type { RenderFrame, RendererOptions, RendererStats, TableRenderer } from '@v2/contracts'
import { projectPoint3, rayFromScreen, transformPoint3 } from '@v2/math'
import { buildCamera, type CameraMatrices } from './camera'
import { createDevice, type DeviceContext } from './device'
import { createPipelines, type Pipelines } from './pipelines'
import { SceneResources, type GpuObject } from './resources'
import { RenderTargets } from './targets'
import { EnvironmentPass } from './environment-pass'
import { buildEnvironmentObjects } from './environment-objects'
import { pickObjects } from './picking'

/** GPU draw submission only. Layout, transitions, and pointer policy live above it. */
class WebGpuRenderer implements TableRenderer {
  private readonly resources: SceneResources
  private readonly targets: RenderTargets
  private readonly frameBuffer: GPUBuffer
  private readonly frameBinding: GPUBindGroup
  private readonly shadowFrameBinding: GPUBindGroup
  private camera?: CameraMatrices
  private cssWidth = 1
  private cssHeight = 1
  private pixelRatio: number
  private disposed = false
  private drawCalls = 0
  private triangles = 0
  private frameMs = 0

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly gpu: DeviceContext,
    private readonly pipelines: Pipelines,
    private readonly environmentPass: EnvironmentPass,
    options: RendererOptions,
    private readonly isLost: () => boolean,
  ) {
    const { device, format } = gpu
    this.pixelRatio = options.pixelRatio ?? globalThis.devicePixelRatio ?? 1
    this.resources = new SceneResources(device, pipelines)
    this.targets = new RenderTargets(device, format)
    this.frameBuffer = device.createBuffer({ label: 'Camera and lighting', size: 160, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST })
    const shadowSampler = device.createSampler({ label: 'Shadow comparison sampler', compare: 'less-equal', magFilter: 'linear', minFilter: 'linear' })
    this.frameBinding = device.createBindGroup({ label: 'Camera and shadows', layout: pipelines.frameLayout, entries: [
      { binding: 0, resource: { buffer: this.frameBuffer } },
      { binding: 1, resource: this.targets.shadowView },
      { binding: 2, resource: shadowSampler },
    ] })
    this.shadowFrameBinding = device.createBindGroup({ label: 'Shadow camera', layout: pipelines.shadowFrameLayout, entries: [{ binding: 0, resource: { buffer: this.frameBuffer } }] })
    this.resize(canvas.clientWidth || 1, canvas.clientHeight || 1)
  }

  draw(frame: RenderFrame): void {
    if (this.disposed || this.isLost() || !this.gpu.ownsCanvas()) return
    const started = performance.now()
    const environment = frame.environment ?? { preset: 'simple', time: 0 }
    const bounds = environment.bounds ?? { center: [0, 0, 0] as const, width: 17, depth: 12 }
    this.resources.sync([...frame.objects, ...buildEnvironmentObjects(environment.preset, bounds, environment.time)])
    this.camera = buildCamera(frame.camera, this.cssWidth, this.cssHeight, frame.objects)
    this.camera.uniforms[35] = this.canvas.width / this.cssWidth
    const { device, context } = this.gpu
    const worldObjects: GpuObject[] = []
    const foregroundObjects: GpuObject[] = []
    for (const object of this.resources.objects.values()) {
      (object.source.layer === 'foreground' ? foregroundObjects : worldObjects).push(object)
    }
    device.queue.writeBuffer(this.frameBuffer, 0, this.camera.uniforms.buffer)
    const encoder = device.createCommandEncoder({ label: 'Table frame' })
    const shadow = encoder.beginRenderPass({ label: 'Directional shadows', colorAttachments: [], depthStencilAttachment: {
      view: this.targets.shadowView, depthLoadOp: 'clear', depthClearValue: 1, depthStoreOp: 'store',
    } })
    shadow.setPipeline(this.pipelines.shadow)
    shadow.setBindGroup(0, this.shadowFrameBinding)
    for (const object of worldObjects) if (object.source.surface !== 'region') this.drawObject(shadow, object, true)
    shadow.end()
    this.environmentPass.draw(encoder, this.targets.colorView, this.camera, this.canvas.width, this.canvas.height, {
      preset: environment.preset, time: environment.time, center: bounds.center, tableSize: [bounds.width, bounds.depth],
    }, { depthView: this.targets.depthView, shadowView: this.targets.shadowView })
    const canvasView = context.getCurrentTexture().createView()
    const hasForeground = foregroundObjects.length > 0
    const color = encoder.beginRenderPass({
      label: 'Lit table with MSAA resolve',
      colorAttachments: [{ view: this.targets.colorView, ...(hasForeground ? {} : { resolveTarget: canvasView }), loadOp: 'load', storeOp: hasForeground ? 'store' : 'discard' }],
      depthStencilAttachment: { view: this.targets.depthView, depthLoadOp: 'load', depthStoreOp: 'discard' },
    })
    color.setPipeline(this.pipelines.main)
    color.setBindGroup(0, this.frameBinding)
    this.triangles = 1
    for (const object of worldObjects) {
      this.drawObject(color, object, false)
      this.triangles += object.mesh.indexCount / 3
    }
    color.end()
    if (hasForeground) {
      // Reuse the same geometry, artwork, camera and multisampled attachments.
      // A fresh depth buffer keeps camera-held cards above the world while
      // preserving their physical overlap with one another.
      const foreground = encoder.beginRenderPass({
        label: 'Camera-held pieces with MSAA resolve',
        colorAttachments: [{ view: this.targets.colorView, resolveTarget: canvasView, loadOp: 'load', storeOp: 'discard' }],
        depthStencilAttachment: { view: this.targets.depthView, depthLoadOp: 'clear', depthClearValue: 1, depthStoreOp: 'discard' },
      })
      foreground.setPipeline(this.pipelines.main)
      foreground.setBindGroup(0, this.frameBinding)
      for (const object of foregroundObjects) {
        this.drawObject(foreground, object, false)
        this.triangles += object.mesh.indexCount / 3
      }
      foreground.end()
    }
    device.queue.submit([encoder.finish()])
    this.drawCalls = worldObjects.length * 2 + foregroundObjects.length + 1
    this.frameMs = performance.now() - started
  }

  private drawObject(pass: GPURenderPassEncoder, object: GpuObject, shadow: boolean): void {
    pass.setBindGroup(1, shadow ? object.shadowBinding : object.mainBinding)
    pass.setVertexBuffer(0, object.mesh.vertex)
    pass.setIndexBuffer(object.mesh.index, 'uint32')
    pass.drawIndexed(object.mesh.indexCount)
  }

  pick(x: number, y: number, mode: 'action' | 'inspect' = 'action'): string | null {
    if (!this.camera || this.disposed || x < 0 || y < 0 || x > this.cssWidth || y > this.cssHeight) return null
    const ray = rayFromScreen(x, y, this.cssWidth, this.cssHeight, this.camera.inverseViewProjection)
    return pickObjects(ray, this.resources.objects.values(), mode)
  }

  project(id: string, options: { hideBehindHand?: boolean } = {}): readonly [number, number] | null {
    const object = this.resources.objects.get(id)
    if (!object || !this.camera || this.disposed) return null
    const topCenter = transformPoint3([0, 0.5, 0], object.model)
    const projected = projectPoint3(topCenter, this.camera.viewProjection, this.cssWidth, this.cssHeight)
    if (projected.visible && options.hideBehindHand) {
      // Count badges only need hand occlusion. Avoid ray-testing every route and
      // world component once per badge on every animation frame.
      const hand = [...this.resources.objects.values()].filter(candidate => candidate.source.layer === 'foreground')
      if (hand.length && pickObjects(rayFromScreen(projected.x, projected.y, this.cssWidth, this.cssHeight, this.camera.inverseViewProjection), hand, 'inspect')) return null
    }
    return projected.visible ? [projected.x, projected.y] : null
  }

  resize(width: number, height: number, pixelRatio = this.pixelRatio): void {
    if (this.disposed || this.isLost() || !this.gpu.ownsCanvas()) return
    this.cssWidth = Math.max(1, width)
    this.cssHeight = Math.max(1, height)
    this.pixelRatio = Math.min(2, Math.max(0.5, pixelRatio))
    const limit = this.gpu.device.limits.maxTextureDimension2D
    const actualWidth = Math.min(limit, Math.max(1, Math.round(this.cssWidth * this.pixelRatio)))
    const actualHeight = Math.min(limit, Math.max(1, Math.round(this.cssHeight * this.pixelRatio)))
    if (this.canvas.width !== actualWidth) this.canvas.width = actualWidth
    if (this.canvas.height !== actualHeight) this.canvas.height = actualHeight
    this.targets.resize(actualWidth, actualHeight)
  }

  stats(): RendererStats {
    return {
      backend: 'WebGPU', adapter: this.gpu.adapterName, drawCalls: this.drawCalls,
      triangles: this.triangles, objects: this.resources.objects.size,
      textures: this.resources.textureCount + (this.disposed ? 0 : 3), buffers: this.resources.bufferCount + (this.disposed ? 0 : 2),
      frameMs: this.frameMs, width: this.canvas.width, height: this.canvas.height,
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.drawCalls = 0
    this.triangles = 0
    this.camera = undefined
    this.resources.dispose()
    this.targets.dispose()
    this.frameBuffer.destroy()
    this.environmentPass.dispose()
    this.gpu.dispose()
  }
}

export async function createWebGpuRenderer(canvas: HTMLCanvasElement, options: RendererOptions = {}): Promise<TableRenderer> {
  let lost = false
  const seenErrors = new Set<string>()
  const report = (message: string) => {
    if (seenErrors.has(message)) return
    seenErrors.add(message)
    options.onError?.(message)
  }
  const gpu = await createDevice(canvas, report, (message) => { lost = true; options.onDeviceLost?.(message) })
  let environmentPass: EnvironmentPass | undefined
  try {
    const pipelines = await createPipelines(gpu.device, gpu.format)
    environmentPass = await EnvironmentPass.create(gpu.device, gpu.format)
    if (!gpu.ownsCanvas()) throw new DOMException('Renderer initialization was superseded by a newer request for this canvas.', 'AbortError')
    return new WebGpuRenderer(canvas, gpu, pipelines, environmentPass, options, () => lost)
  } catch (error) {
    environmentPass?.dispose()
    gpu.dispose()
    throw error
  }
}
